import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { MessageSquareText, Plus, Pencil, Trash2, Check, X, ChevronDown, Loader2 } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { toast } from "sonner";

// Combobox "Testi rapidi": elenco di testi predefiniti, inserimento nel corpo,
// creazione/modifica/cancellazione. Usa il pattern popover del progetto.
export default function TestiRapidiPicker({ onInsert, testId = "testi-rapidi" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("list"); // list | form
  const [editing, setEditing] = useState(null);
  const [titolo, setTitolo] = useState("");
  const [testo, setTesto] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/studio/testi-rapidi"); setItems(data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const startNew = () => { setEditing(null); setTitolo(""); setTesto(""); setMode("form"); };
  const startEdit = (it) => { setEditing(it); setTitolo(it.titolo); setTesto(it.testo); setMode("form"); };

  const save = async () => {
    if (!titolo.trim() || !testo.trim()) { toast.error("Inserisci titolo e testo"); return; }
    setSaving(true);
    try {
      if (editing) await api.patch(`/studio/testi-rapidi/${editing.id}`, { titolo, testo });
      else await api.post("/studio/testi-rapidi", { titolo, testo });
      toast.success(editing ? "Testo rapido aggiornato" : "Testo rapido salvato");
      setMode("list"); setEditing(null); setTitolo(""); setTesto(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (it) => {
    if (!window.confirm(`Eliminare il testo rapido "${it.titolo}"?`)) return;
    try { await api.delete(`/studio/testi-rapidi/${it.id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pick = (it) => { onInsert?.(it.testo); setOpen(false); };

  return (
    <div className="relative">
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => { setMode("list"); setOpen((o) => !o); }} data-testid={`${testId}-trigger`}
          className="inline-flex items-center gap-1.5 text-xs text-amber-300 rounded-md border border-amber-500/40 px-2.5 py-1.5 hover:bg-amber-500/10 transition-colors">
          <MessageSquareText className="h-3.5 w-3.5" /> Testi rapidi <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={() => { setOpen(true); startNew(); }} data-testid={`${testId}-add`} title="Nuovo testo rapido"
          className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 transition-colors">
          <Plus className="h-4 w-4" />
        </button>
      </div>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 right-0 w-[30rem] max-w-[calc(100vw-1.5rem)] rounded-md border border-slate-700 bg-[#121824] shadow-xl" data-testid={`${testId}-panel`}>
            {mode === "list" ? (
              <div className="max-h-72 overflow-y-auto p-1.5">
                {loading ? (
                  <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-amber-500" /></div>
                ) : items.length === 0 ? (
                  <p className="text-xs text-slate-500 text-center py-5">Nessun testo rapido salvato.</p>
                ) : (
                  <TooltipProvider delayDuration={300}>
                    {items.map((it) => (
                      <div key={it.id} data-testid={`${testId}-item-${it.id}`} className="group flex items-center gap-1 rounded hover:bg-slate-800/60">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" onClick={() => pick(it)} data-testid={`${testId}-insert-${it.id}`}
                              className="flex-1 text-left px-2 py-1.5 min-w-0">
                              <div className="text-sm text-slate-100 truncate">{it.titolo}</div>
                              <div className="text-[11px] text-slate-500 line-clamp-2">{it.testo}</div>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="left" align="start" sideOffset={8}
                            className="max-w-sm bg-popover text-popover-foreground border border-border shadow-xl"
                            data-testid={`${testId}-tooltip-${it.id}`}>
                            <div className="text-sm font-semibold mb-1 break-words">{it.titolo}</div>
                            <div className="text-xs whitespace-pre-wrap break-words max-h-64 overflow-y-auto opacity-90">{it.testo}</div>
                          </TooltipContent>
                        </Tooltip>
                        <button type="button" onClick={() => startEdit(it)} data-testid={`${testId}-edit-${it.id}`} title="Modifica"
                          className="shrink-0 h-7 w-7 flex items-center justify-center text-slate-400 hover:text-amber-400"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => del(it)} data-testid={`${testId}-del-${it.id}`} title="Elimina"
                          className="shrink-0 h-7 w-7 flex items-center justify-center text-slate-400 hover:text-red-400 mr-0.5"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </TooltipProvider>
                )}
                <button type="button" onClick={startNew} data-testid={`${testId}-new`}
                  className="mt-1 w-full flex items-center gap-1.5 rounded px-2 py-1.5 text-xs text-amber-400 hover:bg-amber-500/10 border-t border-slate-800">
                  <Plus className="h-3.5 w-3.5" /> Nuovo testo rapido
                </button>
              </div>
            ) : (
              <div className="p-2.5 space-y-2">
                <div className="text-xs font-semibold text-slate-200">{editing ? "Modifica testo rapido" : "Nuovo testo rapido"}</div>
                <Input value={titolo} onChange={(e) => setTitolo(e.target.value)} placeholder="Titolo" data-testid={`${testId}-form-titolo`}
                  className="h-8 bg-slate-900/60 border-slate-700 text-sm" />
                <textarea value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Testo…" data-testid={`${testId}-form-testo`} rows={5}
                  className="w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-amber-500 resize-y" />
                <div className="flex gap-2">
                  <Button size="sm" onClick={save} disabled={saving} data-testid={`${testId}-form-save`} className="flex-1 h-8 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />} Salva
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => { setMode("list"); setEditing(null); }} data-testid={`${testId}-form-cancel`} className="flex-1 h-8 border-slate-700 text-slate-300 hover:bg-slate-800">
                    <X className="h-4 w-4 mr-1" /> Annulla
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
